
// export default BuyersAddress;
import React, { useState, useEffect, useCallback } from 'react';

import {
  Mail,
  Phone,
  Trash2,
  Copy,
  CheckCircle,
  XCircle,
  ShoppingBag,
  DollarSign,
  X,
  Eye
} from 'lucide-react';
import "../styles/BuyerAddress.css";
import { GRAPHQL_URL } from "../apolloClient";


const BuyersAddress = () => {

  const [buyers, setBuyers] = useState([]);
  const [filteredBuyers, setFilteredBuyers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    city: ''
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // Show toast message
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  // GraphQL fetch buyers function
  const fetchBuyers = async () => {
    setLoading(true);
    setError(null);

    const query = `
      query {
        getAllBuyers {
          id
          buyerId
          name
          email
          phone
          addresses {
            addressId
            streetAddress
            city
            state
            zipCode
            phone
          }
        }
      }
    `;

    try {
      const res = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });

      const json = await res.json();

      if (json.errors) {
        throw new Error(json.errors[0].message);
      }

      const transformed = json.data.getAllBuyers.map(buyer => ({
        id: buyer.id,
        buyerId: buyer.buyerId,
        name: buyer.name || "Unknown Buyer",
        email: buyer.email || "No email",
        phone: buyer.phone || "No phone",
        addresses: buyer.addresses || [],
        totalOrders: buyer.addresses?.length || 0,
        totalSpent: (buyer.addresses?.length || 0) * 150,
        memberSince: new Date().toISOString().split("T")[0],
      }));

      setBuyers(transformed);
      setFilteredBuyers(transformed);
      if (transformed.length > 0) {
        setSelectedBuyer(transformed[0]);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch buyers");
    } finally {
      setLoading(false);
    }
  };

  // Fetch buyers on mount
  useEffect(() => {
    fetchBuyers();
  }, []);

  // Calculate statistics based on GraphQL data
  const stats = {
    totalBuyers: buyers.length,
    totalAddresses: buyers.reduce((acc, buyer) => acc + (buyer.addresses?.length || 0), 0),
    differentCities: [...new Set(buyers.flatMap(b =>
      b.addresses?.map(addr => addr.city).filter(Boolean)
    ))].length,
    activeBuyers: buyers.filter(b => b.addresses?.length > 0).length
  };

  // Handle search and filter
  const handleSearchAndFilter = useCallback(() => {
    let results = buyers;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      results = results.filter(buyer =>
        buyer.name?.toLowerCase().includes(term) ||
        buyer.email?.toLowerCase().includes(term) ||
        buyer.phone?.toLowerCase().includes(term) ||
        buyer.buyerId?.toLowerCase().includes(term) ||
        buyer.addresses?.some(addr =>
          addr.city?.toLowerCase().includes(term) ||
          addr.streetAddress?.toLowerCase().includes(term)
        )
      );
    }

    if (filters.city) {
      results = results.filter(buyer =>
        buyer.addresses?.some(addr =>
          addr.city?.toLowerCase() === filters.city.toLowerCase()
        )
      );
    }

    setFilteredBuyers(results);
    setCurrentPage(1); // Reset to first page on search
  }, [searchTerm, filters, buyers]);

  // Apply filters when search or filters change
  useEffect(() => {
    handleSearchAndFilter();
  }, [handleSearchAndFilter]);

  // Pagination calculations
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(filteredBuyers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedBuyers = filteredBuyers.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    if (id === 'buyerId') {
      showToast('Buyer ID copied to clipboard!', 'success');
    } else if (id === 'allAddresses') {
      showToast('All addresses copied to clipboard!', 'success');
    } else {
      showToast('Address copied to clipboard!', 'success');
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    try {
      const headers = ['Buyer ID', 'Name', 'Email', 'Phone', 'Total Addresses', 'Total Orders', 'Total Spent', 'Member Since'];
      const csvData = buyers.map(buyer => [
        buyer.buyerId,
        buyer.name,
        buyer.email,
        buyer.phone,
        buyer.addresses?.length || 0,
        buyer.totalOrders,
        `$${buyer.totalSpent.toFixed(2)}`,
        buyer.memberSince
      ]);

      let csvContent = headers.join(',') + '\n';
      csvData.forEach(row => {
        csvContent += row.map(cell => `${cell}`).join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `buyers-addresses-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showToast('CSV exported successfully!', 'success');
    } catch (err) {
      console.error('Error exporting CSV:', err);
      showToast('Failed to export CSV', 'error');
    }
  };

  // Get all unique cities for filters
  const cities = [...new Set(buyers.flatMap(buyer =>
    buyer.addresses?.map(addr => addr.city).filter(Boolean)
  ))];



  // Delete buyer
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this buyer?")) return;

    const mutation = `
      mutation DeleteBuyer($id: ID!) {
        deleteBuyer(id: $id)
      }
    `;

    try {
      const res = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: mutation,
          variables: { id: selectedBuyer.id },
        }),
      });

      const json = await res.json();
      if (json.errors) throw new Error(json.errors[0].message);

      showToast("Buyer deleted successfully!");
      fetchBuyers();
      setShowDeleteConfirm(false);
    } catch (err) {
      showToast(err.message || "Failed to delete buyer", "error");
    }
  };



  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading buyers data...</p>
    </div>
  );

  if (error) return (
    <div className="error-container">
      <h3>Error loading data</h3>
      <p>{error}</p>
      <button onClick={() => fetchBuyers()} className="btn-retry">
        Retry
      </button>
    </div>
  );

  return (
    <div className="sheet-container">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast ${toast.type}`}>
          <div className="toast-content">
            {toast.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <XCircle size={20} />
            )}
            <span>{toast.message}</span>
          </div>
          <button className="toast-close" onClick={() => setToast({ show: false, message: '', type: '' })}>
            <X size={16} />
          </button>
        </div>
      )}

      <div className="sheet-header">
        <h1>
          <span className="sheet-icon">👥</span>
          Buyers Address Directory
        </h1>
        <div className="sheet-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by ID, name, email, phone, city or address..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="sheet-search"
            />

          </div>
          <div className="action-buttons">
            <div className="filter-group-header">
              <select
                value={filters.city}
                onChange={(e) => {
                  setFilters({ city: e.target.value });
                  setCurrentPage(1);
                }}
                className="filter-select-header"
              >
                <option value="">All Cities</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            <button className="refresh-btn" onClick={fetchBuyers}>
              ↻ Refresh
            </button>
            <button className="export-btn" onClick={handleExportCSV}>
              📥 Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="sheet-summary">
        <div className="summary-card">
          <span className="summary-label">Total Buyers</span>
          <span className="summary-value">{stats.totalBuyers}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total Addresses</span>
          <span className="summary-value">{stats.totalAddresses}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Unique Cities</span>
          <span className="summary-value">{stats.differentCities}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Showing</span>
          <span className="summary-value">
            {filteredBuyers.length > 0
              ? `${startIndex + 1}-${Math.min(startIndex + ITEMS_PER_PAGE, filteredBuyers.length)} of ${filteredBuyers.length}`
              : "0 of 0"
            }
          </span>
        </div>
      </div>

      <div className="sheet-table-container">
        <div className="sheet-table-wrapper">
          <table className="sheet-table">
            <thead>
              <tr>
                <th>Buyer ID</th>
                <th>Buyer Name</th>
                <th>Contact Info</th>
                <th>Street Address</th>
                <th>City & State</th>
                <th className="numeric">Zip</th>
                <th className="numeric">Addresses</th>
                <th>Activity</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBuyers.length > 0 ? (
                paginatedBuyers.map(buyer => (
                  <tr key={buyer.id}>
                    <td>
                      <span className="buyer-id-badge">{buyer.buyerId}</span>
                    </td>
                    <td>
                      <span className="buyer-name-text">{buyer.name}</span>
                    </td>
                    <td>
                      <div className="contact-info">
                        <div className="contact-row"><Mail size={12} /> {buyer.email}</div>
                        <div className="contact-row"><Phone size={12} /> {buyer.phone}</div>
                      </div>
                    </td>
                    <td>
                      <span className="address-text">
                        {buyer.addresses && buyer.addresses.length > 0 ? buyer.addresses[0].streetAddress : "No address"}
                      </span>
                    </td>
                    <td>
                      <span className="city-state-text">
                        {buyer.addresses && buyer.addresses.length > 0 ? `${buyer.addresses[0].city}, ${buyer.addresses[0].state}` : "-"}
                      </span>
                    </td>
                    <td className="numeric">
                      {buyer.addresses && buyer.addresses.length > 0 ? buyer.addresses[0].zipCode : "-"}
                    </td>
                    <td className="numeric">
                      <span className={`count-badge ${buyer.addresses?.length > 0 ? 'completed' : 'pending'}`}>
                        {buyer.addresses?.length || 0}
                      </span>
                    </td>
                    <td>
                      <div className="activity-info">
                        <div className="spent-val">${buyer.totalSpent.toFixed(2)}</div>
                        <div className="orders-count">{buyer.totalOrders} orders</div>
                      </div>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <button
                          className="btn-icon-view"
                          title="View Details"
                          onClick={() => { setSelectedBuyer(buyer); setShowViewModal(true); }}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn-icon-copy"
                          title="Copy Address"
                          onClick={() => {
                            if (buyer.addresses && buyer.addresses.length > 0) {
                              const addr = buyer.addresses[0];
                              const text = `${addr.streetAddress}, ${addr.city}, ${addr.state} ${addr.zipCode}`;
                              copyToClipboard(text, `addr-${buyer.id}`);
                            } else {
                              showToast("No address to copy", "error");
                            }
                          }}
                        >
                          <Copy size={16} />
                        </button>
                        <button
                          className="btn-icon-danger"
                          title="Delete"
                          onClick={() => { setSelectedBuyer(buyer); setShowDeleteConfirm(true); }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="no-data-cell">
                    <div className="empty-state">
                      <span className="empty-icon">📭</span>
                      <p>No buyers found matching your criteria</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="simple-pagination">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Previous
          </button>
          <div className="page-info">
            Page {currentPage} of {totalPages}
            <div className="showing-info">
              Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredBuyers.length)} of {filteredBuyers.length} buyers
            </div>
          </div>
          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && selectedBuyer && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal view-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Buyer Details</h3>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="buyer-details-container">
                <div className="buyer-profile-summary">
                  <div className="profile-avatar-large">
                    {selectedBuyer.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="profile-text">
                    <h4>{selectedBuyer.name}</h4>
                    <p className="buyer-id-full">Buyer ID: {selectedBuyer.buyerId}</p>
                  </div>
                </div>

                <div className="details-grid">
                  <div className="detail-item">
                    <label><Mail size={14} /> Email</label>
                    <p>{selectedBuyer.email}</p>
                  </div>
                  <div className="detail-item">
                    <label><Phone size={14} /> Phone</label>
                    <p>{selectedBuyer.phone || 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <label><ShoppingBag size={14} /> Total Orders</label>
                    <p>{selectedBuyer.totalOrders}</p>
                  </div>
                  <div className="detail-item">
                    <label><DollarSign size={14} /> Total Spent</label>
                    <p>${selectedBuyer.totalSpent.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="addresses-section">
                <h5>Stored Addresses ({selectedBuyer.addresses.length})</h5>
                {selectedBuyer.addresses.length > 0 ? (
                  <div className="address-list-compact">
                    {selectedBuyer.addresses.map((addr, idx) => (
                      <div key={idx} className="address-card-compact">
                        <p className="addr-main">{addr.streetAddress}</p>
                        <p className="addr-sub">{addr.city}, {addr.state} {addr.zipCode}</p>
                        {addr.phone && <p className="addr-contact"><Phone size={10} /> {addr.phone}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-address-text">No addresses found.</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Buyer Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="warning-icon">
                <Trash2 size={48} color="#ef4444" />
              </div>
              <p>Are you sure you want to delete <strong>{selectedBuyer?.name}</strong>?</p>
              <p className="warning-text">This will permanently delete all buyer information and addresses.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDelete}>
                Delete Buyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyersAddress;